import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  ArrowRight, Truck, Shield, MapPin, Clock, Award, KeyRound,
} from "lucide-react";

const HERO =
  "https://images.pexels.com/photos/34539243/pexels-photo-34539243.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SECONDARY =
  "https://images.pexels.com/photos/16471933/pexels-photo-16471933.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function ServiceConvoyage() {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="service-convoyage-page">
      <Header variant="convoyage" />

      {/* HERO */}
      <section
        className="relative pt-32 pb-32 overflow-hidden grain"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(5,5,5,.9) 0%, rgba(5,5,5,.6) 60%, rgba(5,5,5,.3) 100%), url(${HERO})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 reveal">
          <div className="text-xs tracking-[0.55em] gold uppercase mb-6">
            Service #01 — Convoyage
          </div>
          <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-3xl">
            Livrez votre <span className="gold">véhicule</span><br />
            partout en France.
          </h1>
          <p className="mt-8 text-lg text-gray-300 max-w-2xl leading-relaxed">
            Roulant ou non-roulant, boîte manuelle ou automatique, citadine à
            SUV — nous convoyons votre auto de A à B avec discrétion,
            ponctualité et un soin absolu.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/convoyage"
              data-testid="convoyage-cta-primary"
              className="btn-gold inline-flex items-center gap-2"
            >
              Demander un devis <ArrowRight size={18} />
            </Link>
            <a
              href="#tarifs"
              className="btn-outline-gold inline-flex items-center gap-2"
            >
              Voir les tarifs
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.5em] gold uppercase text-center">
            Ce qui nous distingue
          </div>
          <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl text-center">
            L'exigence à chaque kilomètre.
          </h2>

          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="p-8 border border-white/10 bg-[#0a0a0a] hover:border-[color:var(--gold)]/40 transition-colors duration-500"
              >
                <Icon size={28} className="gold mb-5" />
                <div className="font-serif text-2xl text-white mb-2">{title}</div>
                <p className="text-sm text-gray-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing block */}
      <section id="tarifs" className="py-24 border-t border-white/5 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs tracking-[0.5em] gold uppercase">Tarifs</div>
            <h3 className="mt-3 font-serif text-white text-4xl mb-8">
              Un prix juste, dégressif par palier.
            </h3>
            <PricingRow label="0 – 100 km" price="1,30 €" per="/ km" highlight />
            <PricingRow label="101 – 300 km" price="1,10 €" per="/ km" />
            <PricingRow label="+ 300 km" price="1,00 €" per="/ km" />
            <div className="mt-8 space-y-3 text-sm text-gray-400">
              <p>
                <b className="gold">Acompte de 30%</b> à la réservation via
                Stripe, solde à régler avant ou à la livraison du véhicule.
              </p>
              <p>
                Les péages, parkings et prestations supplémentaires éventuels
                sont ajoutés au solde final.
              </p>
            </div>
            <Link
              to="/convoyage"
              className="btn-gold inline-flex items-center gap-2 mt-8"
            >
              Simuler mon devis <ArrowRight size={16} />
            </Link>
          </div>
          <div
            className="h-[500px] border border-white/10"
            style={{
              backgroundImage: `linear-gradient(180deg, rgba(5,5,5,.15) 0%, rgba(5,5,5,.5) 100%), url(${SECONDARY})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>
      </section>

      {/* Process */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.5em] gold uppercase text-center">
            Notre méthode
          </div>
          <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl text-center mb-16">
            Quatre étapes, zéro surprise.
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative">
                <div className="font-serif text-6xl gold opacity-30">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-serif text-xl text-white mt-2">{s.title}</div>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                  {s.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 border-t border-white/5 bg-black relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 30% 40%, rgba(212,175,55,0.15), transparent 60%)",
          }}
        />
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center relative">
          <div className="text-xs tracking-[0.5em] gold uppercase">Prêt ?</div>
          <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl">
            Votre véhicule mérite le meilleur convoyage.
          </h2>
          <p className="mt-4 text-gray-400 max-w-2xl mx-auto">
            Devis instantané en ligne, validation manuelle par nos soins,
            paiement sécurisé Stripe.
          </p>
          <Link
            to="/convoyage"
            data-testid="convoyage-cta-final"
            className="btn-gold inline-flex items-center gap-2 mt-8"
          >
            Réserver mon convoyage <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const FEATURES = [
  {
    icon: Shield,
    title: "Sécurité totale",
    text: "Chauffeurs expérimentés, véhicules assurés en tous risques pendant tout le trajet.",
  },
  {
    icon: KeyRound,
    title: "Roulant ou non",
    text: "Camion plateau pour les véhicules non-roulants, convoyage classique pour les autres.",
  },
  {
    icon: MapPin,
    title: "Partout en France",
    text: "De Lille à Marseille, de Brest à Strasbourg. Zones DOM sur devis.",
  },
  {
    icon: Clock,
    title: "Ponctualité premium",
    text: "Créneau de livraison respecté à la minute. Communication en temps réel.",
  },
  {
    icon: Award,
    title: "Discrétion garantie",
    text: "Manipulation soignée, aucune trace, restitution du véhicule impeccable.",
  },
  {
    icon: Truck,
    title: "Toutes les catégories",
    text: "Citadines, berlines, SUV, break, utilitaires, sportives et véhicules de luxe.",
  },
];

const STEPS = [
  { title: "Devis en ligne", text: "Remplissez votre demande — le prix s'affiche en temps réel." },
  { title: "Acompte 30%", text: "Paiement sécurisé Stripe pour bloquer votre créneau." },
  { title: "Validation manuelle", text: "Arnaud confirme votre demande personnellement." },
  { title: "Livraison", text: "Votre véhicule arrive à destination, solde réglé sur place." },
];

function PricingRow({ label, price, per, highlight }) {
  return (
    <div
      className={`flex items-baseline justify-between py-4 border-b border-white/5 ${
        highlight ? "border-b-[color:var(--gold)]/30" : ""
      }`}
    >
      <span className={highlight ? "text-white" : "text-gray-300"}>{label}</span>
      <span>
        <span className={`font-serif text-2xl ${highlight ? "gold" : "text-white"}`}>
          {price}
        </span>
        <span className="text-gray-500 text-sm ml-2">{per}</span>
      </span>
    </div>
  );
}
